import React from "react";
import { Link } from "react-router-dom";
import {
  ScrollText,
  Sliders,
  AlertTriangle,
  CalendarClock,
  Layers3,
  HelpCircle,
} from "lucide-react";

// Exported so the parent page can inject FAQPage JSON-LD into <SEO />
export const calculatorFaqs = [
  {
    q: "Is this Florida claim calculator accurate?",
    a: "The calculator uses current Florida market replacement-cost ranges for roof, water, wind, interior, and contents losses. It gives you a credible low–high range that&apos;s defensible against carrier estimates — but it&apos;s not a substitute for a licensed adjuster physically inspecting your property. Use it to sanity-check what the insurance company is offering you, then let us verify it with a free, no-obligation inspection.",
  },
  {
    q: "Do I have to enter my contact info to see an estimate?",
    a: "Yes — we ask for your name, email, phone, and property address before revealing the dollar range. Why? Because the estimate is most useful when paired with a real adjuster review. Your info goes straight to Joe and Victoria (the licensed PAs on staff) — we never sell or share it.",
  },
  {
    q: "Will Claim Beast file my claim or talk to my insurance company?",
    a: "Only if you hire us. The calculator and review are free. If you decide to retain Claim Beast as your public adjuster, we work on contingency — meaning we don&apos;t get paid unless you do. Florida law caps PA fees at 20% (10% on hurricane claims for the first year), and that&apos;s the only fee involved.",
  },
  {
    q: "What if my claim was already denied or underpaid?",
    a: "That&apos;s exactly when this calculator is most valuable. If your carrier denied the claim or paid less than the calculator&apos;s low estimate, you almost certainly have grounds to dispute. Florida allows reopening claims, supplemental claims, and appraisal under most policies — we handle all three.",
  },
  {
    q: "How long do I have to file a claim in Florida?",
    a: "For hurricane and windstorm claims, Florida law (§627.70132) gives you 1 year from the date of loss to file a new claim and 18 months to file a supplemental claim. For all other property claims, the deadline is generally 2 years. Miss those windows and your claim is barred forever — even if the damage is obvious.",
  },
  {
    q: "Does the calculator work for commercial property claims?",
    a: "The current version is calibrated for residential homes and condos. Commercial claims (apartment buildings, retail, warehouses, HOAs) involve business interruption, code upgrades, and larger structural losses that need a custom estimate. Call us at (386) 747-9440 for a commercial review.",
  },
  {
    q: "Why is the &quot;Likely Recovery&quot; number lower than the high estimate?",
    a: "The low–high range represents the full statistical band of what a claim like yours could settle for. The Likely Recovery (mid-55% of the range) is a realistic settlement target after typical carrier negotiation — what most Florida policyholders actually walk away with when they have a public adjuster fighting for them.",
  },
];

const SectionHeader = ({ Icon, eyebrow, title }) => (
  <div className="mb-7">
    <div className="flex items-center gap-2 text-[#E11D26]">
      <Icon className="h-4 w-4" />
      <span className="text-xs font-bold tracking-[0.3em] uppercase">
        {eyebrow}
      </span>
    </div>
    <h2
      className="mt-3 text-3xl sm:text-4xl font-black text-white uppercase leading-tight"
      style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
    >
      {title}
    </h2>
  </div>
);

const CalculatorSEOContent = () => {
  return (
    <section
      className="bg-black border-t border-white/10 py-16 sm:py-20"
      data-testid="calculator-seo-content"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 space-y-16">
        {/* 1. How insurance claims are calculated */}
        <div data-testid="seo-how-calculated">
          <SectionHeader
            Icon={ScrollText}
            eyebrow="The Math Behind The Money"
            title="How Florida Insurance Claims Are Actually Calculated"
          />
          <div className="space-y-4 text-white/80 leading-relaxed text-base sm:text-lg">
            <p>
              Every Florida property insurance claim boils down to one
              number:{" "}
              <strong className="text-white">
                replacement cost value (RCV)
              </strong>
              . That&apos;s the dollar amount it would take to put your
              property back the way it was before the loss — using
              like-kind-and-quality materials at today&apos;s prices, with
              today&apos;s labor rates. Carriers calculate RCV using
              estimating software (Xactimate, Symbility, or proprietary
              platforms) that prices every line item: shingles per square,
              drywall per sheet, baseboards per linear foot, refrigerators by
              model.
            </p>
            <p>
              From RCV, two deductions are made before you see a check:{" "}
              <strong className="text-white">depreciation</strong>{" "}
              (calculated by age and condition of the damaged components) and
              your{" "}
              <strong className="text-white">policy deductible</strong>{" "}
              (usually a flat dollar amount for non-hurricane losses, and a
              percentage of dwelling coverage for hurricane losses — often
              2–10% of Coverage A). What remains is your{" "}
              <strong className="text-white">
                actual cash value (ACV)
              </strong>{" "}
              payment. Once you complete repairs and submit receipts, the
              carrier owes the recoverable depreciation back to you — turning
              ACV into the full RCV recovery the policy actually promises.
            </p>
            <p>
              Our calculator estimates the RCV side of that equation using
              current Tampa Bay and Florida-wide market rates. Carriers are
              notorious for underpricing line items (using 2019 labor rates
              in 2026, omitting code-upgrade requirements, missing entire
              damage categories like ceiling joists or attic insulation).
              That gap between what the policy owes and what the carrier
              offers is what a public adjuster gets paid to close.
            </p>
          </div>
        </div>

        {/* 2. What factors affect claim value */}
        <div data-testid="seo-factors">
          <SectionHeader
            Icon={Sliders}
            eyebrow="The Variables That Move The Number"
            title="What Factors Affect Your Claim&apos;s Value"
          />
          <div className="space-y-4 text-white/80 leading-relaxed text-base sm:text-lg">
            <p>
              Two homes with identical damage on the same street can settle
              for wildly different amounts. Here&apos;s why:
            </p>
            <ul className="space-y-3 list-none">
              {[
                {
                  k: "Policy type (HO-3 vs HO-5 vs DP-3)",
                  v: "HO-5 open-peril policies cover virtually everything not specifically excluded. HO-3 and DP-3 (landlord) only cover named perils on contents. Your policy form alone can swing a claim 20–40%.",
                },
                {
                  k: "Replacement cost vs. actual cash value endorsement",
                  v: "If your roof is on ACV-only coverage (common for roofs over 10 years old after 2022 reforms), you\u2019ll see depreciation eat 30–60% of the line item.",
                },
                {
                  k: "Hurricane deductible vs. AOP deductible",
                  v: "A $300,000 home with a 5% hurricane deductible eats $15,000 off the top before the carrier pays a dollar. Knowing which deductible applies (named-storm vs. general) is the single biggest swing factor on storm claims.",
                },
                {
                  k: "Age of components and depreciation schedule",
                  v: "Carriers depreciate roofs, HVAC, water heaters, and flooring on aggressive schedules. A 12-year-old roof might be depreciated 60–80% on the carrier\u2019s estimate. We push back with manufacturer life-expectancy data.",
                },
                {
                  k: "Code-upgrade coverage (Ordinance & Law)",
                  v: "Florida Building Code requires specific upgrades during repair (hurricane straps, secondary water barrier, impact-rated openings). Without O&L coverage, you eat those costs. With it, the carrier owes them.",
                },
                {
                  k: "Matching statute (§626.9744)",
                  v: "Florida law requires carriers to replace undamaged surrounding materials when an exact match isn\u2019t available — meaning a partial roof slope replacement often legally requires the whole roof, and a damaged kitchen cabinet often means replacing the entire run.",
                },
                {
                  k: "Documentation quality",
                  v: "Pre-loss photos, contractor estimates, moisture readings, drone roof scans, and licensed engineer reports can double or triple a carrier\u2019s initial offer. Lack of documentation is the #1 reason claims get underpaid.",
                },
              ].map((item, i) => (
                <li
                  key={i}
                  className="border-l-2 border-[#E11D26] pl-4 py-1"
                >
                  <strong className="text-white block">{item.k}</strong>
                  <span className="text-white/75 text-base">{item.v}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* 3. Signs your claim was underpaid */}
        <div data-testid="seo-underpaid">
          <SectionHeader
            Icon={AlertTriangle}
            eyebrow="Red Flags On Your Settlement"
            title="7 Signs Your Florida Claim Was Underpaid"
          />
          <div className="space-y-4 text-white/80 leading-relaxed text-base sm:text-lg">
            <p>
              If any of these match your situation, your payout is almost
              certainly less than what your policy actually owes:
            </p>
            <ol className="space-y-3 list-none counter-reset-item">
              {[
                "The carrier&apos;s estimate is dramatically lower than the contractor bid you got — and the line items don&apos;t match (missing rooms, missing trades, smaller measurements).",
                "Your settlement check arrived within 7 days of reporting — fast checks are usually low-ball checks designed to close the file before you notice what&apos;s missing.",
                "The carrier denied entire categories of damage (&quot;wear and tear,&quot; &quot;maintenance,&quot; or &quot;pre-existing&quot;) without sending a qualified engineer to inspect.",
                "The estimate uses outdated unit prices (e.g. 2019–2021 Xactimate pricing in a 2026 claim) — Florida labor and material costs are up 35–60% since then.",
                "Code-upgrade items are missing — no secondary water barrier, no hurricane straps, no upgraded electrical to current code, even though Florida law requires them during repair.",
                "Matching statute is being violated — they paid for one slope of roofing, one wall of siding, or three cabinets when the materials are discontinued or visibly different from surrounding undamaged areas.",
                "Recoverable depreciation is buried in fine print — you got an ACV check and don&apos;t realize you&apos;re owed thousands more once repairs are complete.",
              ].map((line, i) => (
                <li
                  key={i}
                  className="flex gap-4 border-b border-white/10 pb-3"
                >
                  <span
                    className="text-[#E11D26] font-black text-2xl leading-none flex-shrink-0"
                    style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span dangerouslySetInnerHTML={{ __html: line }} />
                </li>
              ))}
            </ol>
            <p className="pt-2">
              See two or more on that list? Run your damage through the
              calculator above, then{" "}
              <Link
                to="/#contact"
                className="text-[#E11D26] underline hover:text-white"
              >
                request a free review
              </Link>
              . Florida law gives you 3 years to dispute most carrier
              decisions — but the clock is already running.
            </p>
          </div>
        </div>

        {/* 4. Florida insurance claim deadlines */}
        <div data-testid="seo-deadlines">
          <SectionHeader
            Icon={CalendarClock}
            eyebrow="Don&apos;t Miss These Dates"
            title="Florida Insurance Claim Deadlines (2026)"
          />
          <div className="space-y-4 text-white/80 leading-relaxed text-base sm:text-lg">
            <p>
              Florida statutes set hard deadlines on every property claim.
              Miss them and you lose the right to recover — period, no
              exceptions, even if the damage is catastrophic and obviously
              covered.
            </p>
            <div className="grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10 mt-6">
              {[
                {
                  t: "Hurricane / Windstorm — New Claim",
                  d: "1 year from date of loss (Fla. Stat. §627.70132). Reduced from 2 years by 2022/2023 legislative reforms — this is the most-missed deadline in Florida.",
                },
                {
                  t: "Hurricane / Windstorm — Supplemental Claim",
                  d: "18 months from date of loss. If you discover additional damage after settling (common with roof and water claims), you must file the supplemental within this window.",
                },
                {
                  t: "All Other Property Claims (fire, theft, water, fallen tree)",
                  d: "2 years from date of loss to file a new claim. Supplemental and reopened claims must also fall within this window.",
                },
                {
                  t: "Notice of Intent to Litigate",
                  d: "10 business days&apos; pre-suit notice required before filing a lawsuit against your carrier (Fla. Stat. §627.70152) — separate from the underlying claim deadline.",
                },
                {
                  t: "Carrier\u2019s Duty to Pay",
                  d: "60 days from receipt of a complete proof-of-loss (Fla. Stat. §627.70131(5)(a)). Past 60 days, statutory interest begins accruing on the unpaid amount.",
                },
                {
                  t: "Appraisal Demand",
                  d: "No statutory deadline, but most policies require demand within a reasonable time after the dispute arises — usually within the underlying claim deadline window.",
                },
              ].map((row, i) => (
                <div key={i} className="bg-[#0F0F0F] p-5 sm:p-6">
                  <div
                    className="text-white font-black uppercase text-base tracking-wide leading-tight"
                    style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
                  >
                    {row.t}
                  </div>
                  <p
                    className="mt-2 text-white/70 text-sm leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: row.d }}
                  />
                </div>
              ))}
            </div>
            <p className="pt-2 text-sm text-white/55 italic">
              Citations: Fla. Stat. §§627.70131, 627.70132, 627.70152 (as
              amended through 2024). This is general guidance, not legal
              advice — your policy may contain stricter timelines.
            </p>
          </div>
        </div>

        {/* 5. Common claim types */}
        <div data-testid="seo-claim-types">
          <SectionHeader
            Icon={Layers3}
            eyebrow="What We Handle Every Week"
            title="The Most Common Florida Claim Types"
          />
          <div className="space-y-4 text-white/80 leading-relaxed text-base sm:text-lg">
            <p>
              The calculator above covers the ten damage categories we see
              most often in Tampa Bay. Here&apos;s what each one typically
              looks like and where carriers most often underpay:
            </p>
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              {[
                {
                  t: "Hurricane / Wind Damage",
                  d: "Roof uplift, missing shingles, ridge cap loss, soffit blow-out, fascia separation, interior water from breached envelope. Almost always undervalued on first carrier estimate.",
                  link: "/wind-damage",
                },
                {
                  t: "Roof Damage Claims",
                  d: "Hail, wind, fallen tree, lifted shingles. Florida&apos;s 2022 reforms made roof claims harder — but matching statute and code upgrades still drive significant recoveries.",
                  link: "/roof-claim-denial-florida",
                },
                {
                  t: "Water Damage / Pipe Burst",
                  d: "Sudden discharge from supply lines, dishwasher hoses, water heaters, A/C condensate. Carriers love to deny these as &quot;wear and tear&quot; — fight back with plumber reports.",
                  link: "/water-damage",
                },
                {
                  t: "Mold Remediation",
                  d: "Capped at $10,000 on most Florida policies unless you have a mold endorsement. Often discovered weeks after the underlying water event.",
                  link: "/mold-damage",
                },
                {
                  t: "Fire & Smoke Damage",
                  d: "Structural, contents, smoke odor, soot, and additional living expenses (ALE). Contents valuation is where most fire claims get underpaid by tens of thousands.",
                  link: "/fire-damage",
                },
                {
                  t: "Denied & Underpaid Claims",
                  d: "Reopening a denied claim, disputing a low ball offer, invoking appraisal, filing a Civil Remedy Notice. This is bread and butter for a public adjuster.",
                  link: "/denied-insurance-claim-florida",
                },
                {
                  t: "Hail Damage",
                  d: "Bruised shingles, dented gutters, cracked skylights. Carriers often deny &quot;cosmetic&quot; hail — but hits to roof granules shorten useful life by years.",
                  link: "/hail-damage",
                },
                {
                  t: "Flood Damage",
                  d: "Rising water from storm surge or sustained rainfall. Covered under NFIP / private flood — not standard homeowners — and notoriously complex to document.",
                  link: "/flood-damage",
                },
              ].map((c, i) => (
                <Link
                  key={i}
                  to={c.link}
                  className="block bg-[#0F0F0F] border border-white/10 hover:border-[#E11D26] p-5 transition-colors group"
                >
                  <div
                    className="text-white font-black uppercase text-base tracking-wide group-hover:text-[#E11D26] transition-colors"
                    style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
                  >
                    {c.t}
                  </div>
                  <p
                    className="mt-2 text-white/65 text-sm leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: c.d }}
                  />
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* 6. FAQ */}
        <div data-testid="seo-faq">
          <SectionHeader
            Icon={HelpCircle}
            eyebrow="Frequently Asked Questions"
            title="Claim Calculator FAQ"
          />
          <div className="divide-y divide-white/10 border-t border-b border-white/10">
            {calculatorFaqs.map((item, i) => (
              <details
                key={i}
                className="group py-5"
                data-testid={`seo-faq-item-${i}`}
              >
                <summary className="cursor-pointer list-none flex justify-between items-start gap-4">
                  <span
                    className="text-white font-black uppercase text-base sm:text-lg tracking-wide leading-snug"
                    style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
                    dangerouslySetInnerHTML={{ __html: item.q }}
                  />
                  <span className="text-[#E11D26] text-2xl leading-none flex-shrink-0 group-open:rotate-45 transition-transform">
                    +
                  </span>
                </summary>
                <p
                  className="mt-3 text-white/75 leading-relaxed text-base"
                  dangerouslySetInnerHTML={{ __html: item.a }}
                />
              </details>
            ))}
          </div>
        </div>

        {/* Closing CTA strip */}
        <div
          className="bg-[#0F0F0F] border border-white/10 p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
          data-testid="seo-closing-cta"
        >
          <div>
            <div
              className="text-white font-black uppercase text-xl sm:text-2xl tracking-wide leading-tight"
              style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
            >
              Got A Number? Let&apos;s Verify It.
            </div>
            <p className="mt-2 text-white/65 text-sm sm:text-base">
              Free, no-obligation review by a Florida-licensed public
              adjuster. We&apos;ll tell you straight whether your carrier is
              fair — or leaving thousands on the table.
            </p>
          </div>
          <Link
            to="/#contact"
            data-testid="seo-closing-cta-link"
            className="inline-flex items-center justify-center bg-[#E11D26] hover:bg-white hover:text-[#E11D26] text-white px-7 py-3 font-bold tracking-wider uppercase text-sm transition-colors whitespace-nowrap"
          >
            Request Free Review
          </Link>
        </div>
      </div>
    </section>
  );
};

export default CalculatorSEOContent;
